import { useMemo, useState } from 'react'
import { useAuth } from './useAuth'
import { useSeenWatermark } from './useSeenWatermark'
import { useFrozenValue } from './useFrozenValue'
import type { CrewActivity } from './useCrewActivity'

/**
 * Unread "What's new with your crew" items: feed items whose newest activity is
 * after the users/{uid}/private/crewActivitySeen watermark. Counts events, not
 * actions: an event's item counts again when anyone new marks it or links it. Pass `active` only from the feed
 * while it's on screen; the nav passes false. Created on first sight with "now",
 * so existing history doesn't all show as unread.
 *
 * `seenAtVisitStart` is the watermark as it stood when this tab visit began —
 * it stays put while the open feed advances the stored one, so rows newer than
 * it can keep their unread dot for the rest of the visit. null until known.
 */
export function useCrewActivityUnread(items: CrewActivity[], itemsLoading: boolean, active: boolean) {
  const { signedInUid } = useAuth()
  const [baseline] = useState(() => Date.now())
  const times = useMemo(() => items.map(i => i.at), [items])
  const { unread, lastSeen, loading } = useSeenWatermark({
    uid: signedInUid,
    docName: 'crewActivitySeen',
    times,
    active,
    itemsLoading,
    baseline,
    logTag: 'crew-activity',
  })
  const seenAtVisitStart = useFrozenValue(lastSeen, !loading && lastSeen !== null, signedInUid)
  return { unread, seenAtVisitStart }
}
